import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../ui/Input';
import { iconsPath } from '../../utils/imagePath';
import { Brodcrumb } from '../ui/UtilsLayout';
import UnifiedSearchBar from './UnifiedSearchBar'; // ← ONLY NEW IMPORT

const HelpCenterSection = ({ data, navigate, basePath, seeAllButton }) => {
  // Only split into columns if there are more than 3 articles
  const shouldSplitColumns = data.length > 3;

  const midPoint = Math.ceil(data.length / 2);
  const leftColumn = shouldSplitColumns ? data.slice(0, midPoint) : data;
  const rightColumn = shouldSplitColumns ? data.slice(midPoint) : [];

  const ArticleItem = ({ item, index, totalInColumn, isLeftColumn }) => (
    <div
      onClick={() => navigate(`${basePath}?article=${index}`)}
      className={`py-4 text-gray-300 hover:text-white cursor-pointer transition-colors flex gap-3 items-center
        ${
          index !== totalInColumn - 1
            ? 'border-b border-[#3a3a3a]'
            : 'sm:border-none border-b border-[#3a3a3a]'
        }
      `}
      style={{ color: item?.color }}
    >
      <span>{item?.title}</span>
      {item?.icon && (
        <img
          src={iconsPath.rightArrowRed}
          alt="menu icon"
          className="h-[15px] w-[15px]"
        />
      )}
    </div>
  );

  return (
    <>
      {/* Left Column */}
      <div className="w-full">
        {leftColumn.map((item, index) => (
          <ArticleItem
            key={index}
            item={item}
            index={index}
            totalInColumn={leftColumn.length}
            isLeftColumn={true}
          />
        ))}

        {/* Show "See All" button in left column if not splitting */}
        {!shouldSplitColumns && seeAllButton}
      </div>

      {/* Right Column - Only show if splitting columns */}
      {shouldSplitColumns && rightColumn.length > 0 && (
        <div className="w-full">
          {rightColumn.map((item, index) => (
            <ArticleItem
              key={index}
              item={item}
              index={index + leftColumn.length}
              totalInColumn={rightColumn.length}
              isLeftColumn={false}
            />
          ))}

          {/* See All Button - Inside Right Column */}
          {seeAllButton}
        </div>
      )}
    </>
  );
};

const HelpCenterTemplate = ({
  pageTitle,
  sectionTitle,
  sectionDescription,
  articlesData,
  basePath,
  breadcrumbPath,
  searchPlaceholder = 'Search the Spatial Grid Help Center',
  showArticleCount = true,
  maxDisplayArticles = null, // Set a number to limit initial display
  seeAllLink = null, // Path to navigate when clicking "See All"
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');

  /* ------------------ DYNAMIC BREADCRUMB LOGIC ------------------ */
  const params = new URLSearchParams(location.search);
  const articleIndex = params.get('article');

  const articleTitle =
    articleIndex !== null ? articlesData[articleIndex]?.title : null;

  const breadcrumbData = {
    name: articleTitle || pageTitle,
    item: [
      {
        name: 'Spatial Grid Help Center',
        path: '/help-center',
      },
      ...(articleTitle
        ? [
            {
              name: breadcrumbPath.name,
              path: breadcrumbPath.path,
            },
          ]
        : []),
    ],
  };

  /* ------------------ SEARCH FILTER LOGIC ------------------ */
  const filteredData = useMemo(() => {
    if (!searchTerm) return articlesData;

    return articlesData.filter((item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, articlesData]);

  /* ------------------ DISPLAY LIMIT LOGIC ------------------ */
  const displayData = useMemo(() => {
    if (searchTerm || !maxDisplayArticles) {
      return filteredData;
    }
    return filteredData.slice(0, maxDisplayArticles);
  }, [filteredData, maxDisplayArticles, searchTerm]);

  const hasMore =
    maxDisplayArticles &&
    filteredData.length > maxDisplayArticles &&
    !searchTerm;

  /* -------------------------------------------------------------- */

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 pt-32 md:pt-40 pb-14 text-white">
      <div className="max-w-6xl mx-auto flex flex-col items-start gap-6">
        {/* Dynamic Breadcrumb */}
        <Brodcrumb data={breadcrumbData} />

        {/* Header & Search */}
        <div className="flex flex-col lg:flex-row lg:justify-between items-start lg:items-center gap-5 w-full mb-8">
          <h2 className="text-3xl text-nowrap font-semibold">{pageTitle}</h2>

          {/* ✅ COMPACT VARIANT - Small search bar aligned to the right */}
          <UnifiedSearchBar
            placeholder={searchPlaceholder}
            variant="compact"
            showResultsCount={false}
          />
        </div>

        <div className="flex items-center justify-between w-full">
          <h3 className="text-xl font-semibold">{sectionTitle}</h3>

          {showArticleCount && (
            <span className="text-sm text-gray-400">
              {filteredData.length}{' '}
              {filteredData.length === 1 ? 'article' : 'articles'}
            </span>
          )}
        </div>

        <p className="text-gray-300">{sectionDescription}</p>

        {/* Two Column Layout - Responsive based on article count */}
        <div
          className={`w-full grid gap-0 md:gap-16 mt-4 ${
            displayData.length <= 3
              ? 'grid-cols-1'
              : 'grid-cols-1 sm:grid-cols-2'
          }`}
        >
          {displayData.length > 0 ? (
            <HelpCenterSection
              data={displayData}
              navigate={navigate}
              basePath={basePath}
              seeAllButton={
                hasMore && seeAllLink ? (
                  <button
                    onClick={() => navigate(seeAllLink)}
                    className="py-4 text-red-500 hover:text-red-400 transition-colors flex items-center gap-2 text-base font-medium border-t-0"
                  >
                    <span>See all {filteredData.length} articles</span>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                ) : null
              }
            />
          ) : (
            <p className="text-gray-400 col-span-2 mt-6">No articles found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCenterTemplate;
